/** Teacher tool: assign a Combine practice set (homework) to all, a grade, or a student. */
import React, { useEffect, useState, useCallback } from 'react';
import { Dumbbell, Plus, Trash2, Loader2, Send } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const SUBJECTS = [['math', 'Math'], ['ela', 'ELA'], ['science', 'Science'], ['social_studies', 'Social Studies']];

export default function AssignCombine() {
  const [roster, setRoster] = useState([]);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ scope: 'all', grade: '5', student_id: '', mode: 'subject', subject: 'math', note: '' });

  const load = useCallback(() => {
    api.get('/academy/combine/roster', { headers: auth() }).then((r) => setRoster(r.data.students || [])).catch(() => {});
    api.get('/academy/combine/assignments/manage', { headers: auth() }).then((r) => setList(r.data.assignments || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/academy/combine/assign', {
        scope: form.scope,
        grade: form.scope === 'grade' ? parseInt(form.grade, 10) : null,
        student_id: form.scope === 'student' ? form.student_id : null,
        mode: form.mode,
        subject: form.mode === 'subject' ? form.subject : null,
        note: form.note,
      }, { headers: auth() });
      toast.success('Reps assigned!');
      setForm({ ...form, note: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not assign'); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/academy/combine/assignments/${id}`, { headers: auth() }); load(); } catch { toast.error('Could not remove'); }
  };

  const inp = 'bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-orange-500 rounded';

  return (
    <div className="mt-6 border-2 border-orange-500/40 bg-orange-500/[0.04] p-5 rounded-xl" data-testid="assign-combine-tool">
      <div className="flex items-center gap-2 mb-1"><Dumbbell className="w-4 h-4 text-orange-500" /><h3 className="text-sm font-black uppercase tracking-[0.2em]">Assign Combine Reps</h3></div>
      <p className="text-xs text-zinc-500 mb-4">Push a practice set to all students, a grade, or one scholar as homework. It shows on their Combine page.</p>

      <div className="grid gap-2 sm:grid-cols-2">
        <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className={inp} data-testid="assign-scope">
          <option value="all">All Students</option>
          <option value="grade">Specific Grade</option>
          <option value="student">Specific Student</option>
        </select>
        {form.scope === 'grade' && (
          <select value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className={inp} data-testid="assign-grade">
            {[4, 5, 6, 7, 8, 9].map((g) => <option key={g} value={g}>Grade {g}</option>)}
          </select>
        )}
        {form.scope === 'student' && (
          <select value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className={inp} data-testid="assign-student">
            <option value="">Pick a student…</option>
            {roster.map((s) => <option key={s.id} value={s.id}>{s.name} (G{s.grade_level})</option>)}
          </select>
        )}
        <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={inp} data-testid="assign-mode">
          <option value="subject">Specific Subject</option>
          <option value="today">Today's Lesson</option>
        </select>
        {form.mode === 'subject' && (
          <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inp} data-testid="assign-subject">
            {SUBJECTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        )}
        <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional) e.g. Focus on ratios" className={`${inp} sm:col-span-2`} data-testid="assign-note" />
        <button onClick={submit} disabled={busy} className="sm:col-span-2 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-black py-2.5 text-[10px] font-black uppercase tracking-[0.2em] rounded disabled:opacity-50" data-testid="assign-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Assign Reps
        </button>
      </div>

      {list.length > 0 && (
        <div className="mt-4 border-t border-zinc-800 pt-3 space-y-2" data-testid="assign-list">
          <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 font-black">Active Assignments</div>
          {list.map((a) => (
            <div key={a.id} className="flex items-center gap-3 bg-black/40 rounded-lg px-3 py-2 border border-zinc-800">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium truncate">{a.subject_title} → {a.target_label}</div>
                {a.note && <div className="text-[11px] text-zinc-500 truncate">{a.note}</div>}
              </div>
              <button onClick={() => remove(a.id)} className="text-zinc-500 hover:text-red-400 flex-shrink-0" data-testid={`assign-remove-${a.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
